import { BLOCKER_CODES } from './constants';

const DEFAULT_MAX_SELECTABLE = 5;

const getMaxSelectable = (siteCount) => {
    if (siteCount <= 0) {
        return DEFAULT_MAX_SELECTABLE;
    }

    return Math.min(DEFAULT_MAX_SELECTABLE, siteCount);
};

/** License cap from connector `accountLimit`, or default fallback when absent. */
const resolveMaxSelectable = (data, siteCount) => {
    const accountLimit = Number(data?.accountLimit);
    if (Number.isFinite(accountLimit) && accountLimit > 0) {
        return accountLimit;
    }
    return getMaxSelectable(siteCount);
};

const resolveCompanyName = (site) => {
    const companyName = site.company_name?.trim();
    if (companyName) {
        return companyName;
    }

    const siteName = site.site_name?.trim();
    if (siteName) {
        return siteName;
    }

    if (site.company_id != null) {
        return String(site.company_id);
    }

    return site.site_id ?? '';
};

/** One row per Sage company; `company_id` is the stable UI key when several companies share a `site_id`. */
export const mapSiteToCompany = (site, index = 0) => {
    const companyId =
        site.company_id != null
            ? String(site.company_id)
            : `site-${site.site_id ?? index}-${index}`;

    return {
        id: companyId,
        siteId: site.site_id,
        name: resolveCompanyName(site),
        status: 'Ready to sync',
    };
};

/** Connector site id: `{site_id}|{company_id}` — one entry per selected company row. */
export const buildConnectorSiteId = (siteId, companyId) => {
    if (!siteId || companyId == null || companyId === '') {
        return null;
    }
    return `${siteId}|${companyId}`;
};

export const buildConnectorSiteIds = (companies, selectedCompanyIds) =>
    selectedCompanyIds
        .map((companyId) => {
            const company = companies.find((row) => row.id === companyId);
            return company ? buildConnectorSiteId(company.siteId, company.id) : null;
        })
        .filter(Boolean);

export const mapSessionResponse = (data, sessionId) => {
    if (data?.phase === 'dashboard') {
        return {
            success: false,
            errorCode: BLOCKER_CODES.ONBOARDING_COMPLETED,
        };
    }

    if (data?.phase === 'setup') {
        const sites = data.sites || [];
        const companies = sites.map((site, index) => mapSiteToCompany(site, index));
        const maxSelectable = resolveMaxSelectable(data, sites.length);
        const resolvedSessionId = data.sessionId || sessionId;

        return {
            success: true,
            maxSelectable,
            companies,
            sessionCache: {
                sessionId: resolvedSessionId,
                companies,
                userEmail: data.userEmail,
                userCompanyName: data.userCompanyName,
                maxSelectable,
            },
        };
    }

    return { success: false };
};

export const mapSessionError = (error) => {
    if (error?.response?.status === 401) {
        return { needsSageIdLogin: true };
    }

    if (error?.response?.status === 400) {
        return { success: false, errorCode: BLOCKER_CODES.INVALID_CONTEXT };
    }

    throw error;
};
